import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import api from '../lib/api';

// Fallback (only used before the first API response resolves) — keeps the
// section from flashing empty during initial load.
const FALLBACK = [];

const SIZE_TO_SPAN = {
  small:  'md:col-span-3',
  medium: 'md:col-span-4',
  large:  'md:col-span-5 md:row-span-2',
};

function PhotoFrame({ p }) {
  if (p.rotate_img) {
    return (
      <div className={`relative overflow-hidden ${p.ratio || 'aspect-[4/5]'} bg-void/5`}>
        <img
          src={p.image_data}
          alt={p.caption}
          loading="lazy"
          className="absolute top-1/2 left-1/2 max-w-none"
          style={{
            transform: `translate(-50%, -50%) rotate(${p.rotate_img}deg)`,
            width: 'auto',
            height: '133%',
          }}
        />
        <div className="absolute inset-0 pointer-events-none mix-blend-multiply"
             style={{ background: 'linear-gradient(180deg, rgba(157,78,221,0.05), rgba(36,0,70,0.09))' }} />
      </div>
    );
  }
  return (
    <div className={`relative overflow-hidden ${p.ratio || 'aspect-[4/5]'}`}>
      <img src={p.image_data} alt={p.caption} loading="lazy" className="w-full h-full object-cover object-center" />
      <div className="absolute inset-0 pointer-events-none mix-blend-multiply"
           style={{ background: 'linear-gradient(180deg, rgba(157,78,221,0.05), rgba(36,0,70,0.09))' }} />
    </div>
  );
}

function Polaroid({ p, i }) {
  const spanClass = SIZE_TO_SPAN[p.size] || SIZE_TO_SPAN.medium;
  return (
    <motion.figure
      initial={{ opacity: 0, y: 40, rotate: (p.rotate || 0) * 0.4 }}
      whileInView={{ opacity: 1, y: 0, rotate: p.rotate || 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.85, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ rotate: 0, scale: 1.03, y: -6 }}
      className={`relative bg-paper p-3 pb-16 shadow-[0_20px_50px_-20px_rgba(36,0,70,0.35)] ${spanClass}`}
      style={{ transformOrigin: 'center' }}
      data-testid={`polaroid-${i}`}
    >
      <span
        aria-hidden="true"
        className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-lavender/70 rotate-[-2deg] shadow-sm"
        style={{ backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.25) 0 2px, transparent 2px 6px)' }}
      />
      <PhotoFrame p={p} />
      <figcaption className="absolute left-3 right-3 bottom-3 flex flex-col items-start gap-0.5">
        <span className="mono text-[8px] uppercase tracking-[0.22em] text-orchid whitespace-nowrap">{p.kicker}</span>
        <span className="serif italic text-ink text-[13px] md:text-[15px] leading-none whitespace-nowrap">{p.caption}</span>
      </figcaption>
    </motion.figure>
  );
}

export default function PhotoStrip() {
  const [photos, setPhotos] = useState(FALLBACK);
  useEffect(() => {
    api.get('/photos').then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  if (!photos.length) return null; // no photos yet = section hidden

  return (
    <section
      id="in-the-field"
      className="relative py-28 md:py-40 bg-paper overflow-hidden"
      data-testid="photo-strip-section"
    >
      <div aria-hidden="true" className="absolute -left-40 top-20 w-[560px] h-[560px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(closest-side, rgba(224,170,255,0.55), transparent 70%)' }} />
      <div aria-hidden="true" className="absolute -right-40 bottom-10 w-[520px] h-[520px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(closest-side, rgba(199,125,255,0.35), transparent 70%)' }} />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex items-baseline justify-between mb-14">
          <div className="mono text-xs uppercase tracking-[0.3em] text-orchid">(02.5) — In the field</div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-ink/40 hidden md:block">Contact sheet · un-retouched · on purpose</div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9 }}
          className="serif text-[12vw] md:text-[8vw] leading-[0.9] text-ink mb-6"
        >
          Off <span className="italic text-grape">the clock</span>.
        </motion.h2>
        <p className="mono text-sm md:text-base text-ink/70 max-w-2xl mb-16 md:mb-24">
          I run, I bike, I cook. I collect languages the way other people collect Pokémon cards. Here — the messy, un-optimized parts.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10 items-start">
          {photos.map((p, i) => (
            <Polaroid key={p.id || i} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
